import type { StoreOrder, StoreOrderFulfillment } from "@medusajs/types";

type FulfillmentLabel = {
  id?: string;
  tracking_number?: string | null;
  tracking_url?: string | null;
};

// StoreOrderFulfillment doesn't expose `labels` in the SDK type, but the
// /store/orders endpoint returns it when requested via the `fields` param
// (see queries/orders.ts `ORDER_FIELDS`). Intersect to surface the field
// without a cast.
type FulfillmentWithLabels = StoreOrderFulfillment & {
  labels?: FulfillmentLabel[] | null;
};

function getFulfillments(order: StoreOrder): FulfillmentWithLabels[] {
  return order.fulfillments ?? [];
}

function aftershipUrl(trackingNumber: string): string {
  return `https://track.aftership.com/?tracking-numbers=${encodeURIComponent(trackingNumber)}`;
}

export function OrderShipments({ order }: { order: StoreOrder }) {
  const fulfillments: FulfillmentWithLabels[] = getFulfillments(order).filter(
    (f) => (f.labels?.length ?? 0) > 0,
  );
  if (fulfillments.length === 0) return null;

  return (
    <div className="rounded-3xl bg-white p-8 ring-1 ring-lp-green-mid/20">
      <h2 className="m-0 mb-2 font-bold font-display text-2xl text-lp-green-dk">
        Shipment{fulfillments.length > 1 ? "s" : ""}
      </h2>
      <p className="m-0 mb-6 font-display text-lp-text">
        Track your package with the courier directly or on AfterShip.
      </p>
      <ul className="m-0 list-none space-y-6 p-0">
        {fulfillments.map((fulfillment) => (
          <li key={fulfillment.id}>
            {fulfillment.shipped_at && (
              <div className="mb-2 font-display text-lp-text-lt text-sm">
                Shipped {new Date(fulfillment.shipped_at).toLocaleDateString()}
              </div>
            )}
            <ul className="m-0 list-none space-y-3 p-0">
              {(fulfillment.labels ?? []).map((label, idx) => {
                const trackingNumber = label.tracking_number?.trim();
                if (!trackingNumber) return null;
                const key = label.id ?? `${fulfillment.id}-${idx}`;
                return (
                  <li
                    key={key}
                    className="rounded-2xl bg-lp-green-soft p-4 sm:flex sm:items-center sm:justify-between sm:gap-4"
                  >
                    <div className="font-mono text-lp-text-dk">
                      {trackingNumber}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 sm:mt-0">
                      {label.tracking_url && (
                        <a
                          href={label.tracking_url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="font-display font-semibold text-lp-green-dk underline underline-offset-4 hover:text-lp-green-mid"
                        >
                          Courier site →
                        </a>
                      )}
                      <a
                        href={aftershipUrl(trackingNumber)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="font-display font-semibold text-lp-green-dk underline underline-offset-4 hover:text-lp-green-mid"
                      >
                        Track on AfterShip →
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
